import { useQuery } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { useBranches } from './useBranches';
import { useTimeFrame } from '../contexts/TimeFrameContext';

// Query keys
const HR_QUERY_KEYS = {
  kpis: 'hr-kpis',
  performanceDistribution: 'hr-performance-distribution',
  teamComposition: 'hr-team-composition',
  topPerformers: 'hr-top-performers',
  keyMetrics: 'hr-key-metrics',
  performanceByRole: 'hr-performance-by-role',
  personnelList: 'hr-personnel-list',
} as const;

// Convert time frame to months
const timeFrameToMonths = (timeFrame: string): number => {
  switch (timeFrame) {
    case 'last1Month': return 1;
    case 'last3Months': return 3;
    case 'last6Months': return 6;
    case 'lastYear': return 12;
    default: return 6;
  }
};

// KPIs hook
export const useHRKPIs = (branchId?: number) => {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const months = timeFrameToMonths(timeFrame);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_QUERY_KEYS.kpis, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('No branch selected');
      
      const response = await apiClient.get('/hr/kpis', {
        params: { branchId: effectiveBranchId, months }
      });
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch HR KPIs');
      }
    },
    enabled: !!effectiveBranchId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Performance Distribution hook
export const useHRPerformanceDistribution = (branchId?: number) => {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const months = timeFrameToMonths(timeFrame);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_QUERY_KEYS.performanceDistribution, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('No branch selected');
      
      const response = await apiClient.get('/hr/performance-distribution', {
        params: { branchId: effectiveBranchId, months }
      });
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch performance distribution');
      }
    },
    enabled: !!effectiveBranchId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Team Composition hook
export const useHRTeamComposition = (branchId?: number) => {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const months = timeFrameToMonths(timeFrame);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_QUERY_KEYS.teamComposition, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('No branch selected');
      
      const response = await apiClient.get('/hr/team-composition', {
        params: { branchId: effectiveBranchId, months }
      });
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch team composition');
      }
    },
    enabled: !!effectiveBranchId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Top Performers hook
export const useHRTopPerformers = (branchId?: number) => {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const months = timeFrameToMonths(timeFrame);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_QUERY_KEYS.topPerformers, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('No branch selected');
      
      const response = await apiClient.get('/hr/top-performers', {
        params: { branchId: effectiveBranchId, months }
      });
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch top performers');
      }
    },
    enabled: !!effectiveBranchId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Key Metrics hook
export const useHRKeyMetrics = (branchId?: number) => {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const months = timeFrameToMonths(timeFrame);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_QUERY_KEYS.keyMetrics, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('No branch selected');
      
      const response = await apiClient.get('/hr/key-metrics', {
        params: { branchId: effectiveBranchId, months }
      });
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch key metrics');
      }
    },
    enabled: !!effectiveBranchId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Performance by Role hook
export const useHRPerformanceByRole = (branchId?: number) => {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const months = timeFrameToMonths(timeFrame);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_QUERY_KEYS.performanceByRole, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('No branch selected');
      
      const response = await apiClient.get('/hr/performance-by-role', {
        params: { branchId: effectiveBranchId, months }
      });
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch performance by role');
      }
    },
    enabled: !!effectiveBranchId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Personnel List hook
export const useHRPersonnelList = (branchId?: number, role?: string) => {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const months = timeFrameToMonths(timeFrame);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_QUERY_KEYS.personnelList, effectiveBranchId, role, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('No branch selected');
      
      const params: any = { branchId: effectiveBranchId, months };
      if (role) params.role = role;
      
      const response = await apiClient.get('/hr/personnel-list', { params });
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch personnel list');
      }
    },
    enabled: !!effectiveBranchId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Export all hooks for convenience
export const useHRQueries = () => {
  const kpis = useHRKPIs();
  const performanceDistribution = useHRPerformanceDistribution();
  const teamComposition = useHRTeamComposition();
  const topPerformers = useHRTopPerformers();
  const keyMetrics = useHRKeyMetrics();
  const performanceByRole = useHRPerformanceByRole();
  const personnelList = useHRPersonnelList();

  return {
    kpis,
    performanceDistribution,
    teamComposition,
    topPerformers,
    keyMetrics,
    performanceByRole,
    personnelList,
  };
};